import React from 'react'
import clsx from 'clsx'
import { MenuTreeHierarchyProduct } from '@ancon/wildcat-types'
import ShoppingBagIcon from '@ancon/wildcat-ui/shared/icons/shopping-bag-outline.svg'

import BodyText from '../../../app/components/BodyText'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  outletFiltersOrderFormatSelector,
  outletSelectedOutletIdSelector,
  outletSelectedOutletOrderFormatsSelector,
} from '../../store/outletSelector'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import {
  setProductDetailsModalVisible,
  setSelectedProductId,
} from '../../../product/store/productSlice'
import useAppStore from '../../../../store/hooks/useAppStore'
import {
  checkoutCurrentCheckoutOutletIdSelector,
  checkoutIsWalletTopUpSelector,
  checkoutItemQuantityByProductIdSelector,
  outletSelectedOutletHasCheckoutSelector,
  checkoutIsGiftCardSelector,
} from '../../../checkout/store/checkoutSelectors'
import { outletSetIsVisibleOutletChangeWarningModal } from '../../store/outletSlice'
import ProductCard from '../../../product/components/ProductCard'
import isDefaultProductCardStyle from '../../../product/utils/isDefaultProductCardStyle'
import { checkoutSetSystemProductWarningModalVisible } from '../../../checkout/store/checkoutSlice'
import { CheckoutSystemProductWarningType } from '../../../checkout/types'

import styles from './OutletMenuProductCard.module.scss'

export type OutletMenuProductCardProps = {
  product: MenuTreeHierarchyProduct
}

function OutletMenuProductCard({ product }: OutletMenuProductCardProps) {
  const { productId } = product

  const dispatch = useAppDispatch()
  const store = useAppStore()

  const selectedOrderFormat = useAppSelector(outletFiltersOrderFormatSelector)!
  const outletOderFormats = useAppSelector(
    outletSelectedOutletOrderFormatsSelector,
  )!
  const cartCount = useAppSelector(state =>
    checkoutItemQuantityByProductIdSelector(state, productId),
  )
  const selectedOutletHasCheckout = useAppSelector(
    outletSelectedOutletHasCheckoutSelector,
  )

  const showCartCount = selectedOutletHasCheckout && cartCount > 0

  const defaultProductCardStyle = isDefaultProductCardStyle()

  function handleProductCardOnClick() {
    const checkoutOutletId = checkoutCurrentCheckoutOutletIdSelector(
      store.getState(),
    )
    const selectedOutletId = outletSelectedOutletIdSelector(store.getState())

    dispatch(setSelectedProductId(productId))

    if (checkoutOutletId) {
      const hasWalletTopUpCheckout = checkoutIsWalletTopUpSelector(
        store.getState(),
      )
      const hasGiftCardCheckout = checkoutIsGiftCardSelector(store.getState())

      if (hasWalletTopUpCheckout) {
        // If checkout is wallet top up, show wallet warning modal
        dispatch(
          checkoutSetSystemProductWarningModalVisible(
            CheckoutSystemProductWarningType.AddProductToWalletCheckout,
          ),
        )
      } else if (hasGiftCardCheckout) {
        // If checkout is gift card purchase, show gift card warning modal
        dispatch(
          checkoutSetSystemProductWarningModalVisible(
            CheckoutSystemProductWarningType.AddProductToGiftCardCheckout,
          ),
        )
      } else if (checkoutOutletId === selectedOutletId) {
        // If current checkout and outlet are the same, open product modal
        dispatch(setProductDetailsModalVisible(true))
      } else {
        // If current checkout and outlet are different, show warning modal
        dispatch(outletSetIsVisibleOutletChangeWarningModal(true))
      }
    } else {
      dispatch(setProductDetailsModalVisible(true))
    }
  }

  return (
    <ProductCard
      product={product}
      onClick={handleProductCardOnClick}
      selectedOrderFormat={selectedOrderFormat}
      outletOrderFormats={outletOderFormats}
      highlighted={showCartCount}
      enlargeOnHover
    >
      {showCartCount && (
        <div
          className={clsx(styles.cartCountContainer, {
            [styles.imageOnTopStyle]: !defaultProductCardStyle,
          })}
        >
          <ShoppingBagIcon />
          <BodyText>{cartCount}</BodyText>
        </div>
      )}
    </ProductCard>
  )
}

export default OutletMenuProductCard
